import React, { useEffect, useRef } from "react";
import { TabType } from "../../types";

export interface OpenTab {
  id: TabType;
  label: string;
  icon?: string;
}

interface Props {
  tabs: OpenTab[];
  activePanel: TabType | null;
  onSelect: (id: TabType) => void;
  onClose: (id: TabType) => void;
}

/**
 * VS Code-style editor tab strip. Each open panel is a tab that can be focused
 * or closed via its × button.
 */
const PanelTabs: React.FC<Props> = ({ tabs, activePanel, onSelect, onClose }) => {
  const activeTabRef = useRef<HTMLDivElement | null>(null);

  // Keep the selected tab fully visible: scroll the strip to it whenever the
  // selection changes (e.g. focusing a tab that's off-screen or newly opened
  // past the right edge). block: "nearest" avoids scrolling the page vertically.
  useEffect(() => {
    activeTabRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
      inline: "nearest",
    });
  }, [activePanel]);

  if (tabs.length === 0) return null;

  return (
    <div
      style={{
        display: "flex",
        borderBottom: "1px solid #e0e0e0",
        background: "#f3f3f3",
        overflowX: "auto",
      }}
    >
      {tabs.map((tab) => {
        const isActive = tab.id === activePanel;
        return (
          <div
            key={tab.id}
            ref={isActive ? activeTabRef : undefined}
            onClick={() => onSelect(tab.id)}
            title={tab.label}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "6px",
              padding: "6px 8px 6px 12px",
              cursor: "pointer",
              whiteSpace: "nowrap",
              fontSize: "12px",
              background: isActive ? "white" : "transparent",
              color: isActive ? "#1565c0" : "#555",
              borderRight: "1px solid #e0e0e0",
              borderTop: isActive ? "2px solid #1976D2" : "2px solid transparent",
            }}
          >
            <span style={{ display: "flex", alignItems: "center", gap: "5px" }}>
              {tab.icon && <span style={{ fontSize: "11px" }}>{tab.icon}</span>}
              <span>{tab.label}</span>
            </span>
            <span
              role="button"
              aria-label={`Close ${tab.label}`}
              onClick={(e) => {
                e.stopPropagation();
                onClose(tab.id);
              }}
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: "16px",
                height: "16px",
                borderRadius: "3px",
                color: "#888",
                lineHeight: 1,
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLElement).style.background = "#ddd";
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLElement).style.background = "transparent";
              }}
            >
              ×
            </span>
          </div>
        );
      })}
    </div>
  );
};

export default PanelTabs;
